import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Bell, CheckCircle, ChevronDown, FileText, Home, UserCog, Users } from 'lucide-react';
import { NotificationCard } from '../components/NotificationCard';
import { notifications as seedNotifications } from '../data/notifications';
import { AppNotification } from '../types/notifications';
type NotificationView = 'all' | 'unread' | 'archived';
const VIEWS: {
  key: NotificationView;
  label: string;
}[] = [{
  key: 'all',
  label: 'All'
}, {
  key: 'unread',
  label: 'Unread'
}, {
  key: 'archived',
  label: 'Archived'
}];
export function AlertsPage() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>(seedNotifications);
  const [view, setView] = useState<NotificationView>('all');
  const userMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserMenuOpen]);
  const unreadCount = notifications.filter(item => !item.read && !item.archived).length;
  const markRead = (id: number) => setNotifications(current => current.map(item => item.id === id ? {
    ...item,
    read: true
  } : item));
  const markAllRead = () => setNotifications(current => current.map(item => item.archived ? item : {
    ...item,
    read: true
  }));
  const archive = (id: number) => setNotifications(current => current.map(item => item.id === id ? {
    ...item,
    archived: true,
    read: true
  } : item));
  const restore = (id: number) => setNotifications(current => current.map(item => item.id === id ? {
    ...item,
    archived: false
  } : item));
  const visible = useMemo(() => notifications.filter(item => {
    if (view === 'archived') return Boolean(item.archived);
    if (item.archived) return false;
    if (view === 'unread') return !item.read;
    return true;
  }), [notifications, view]);
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-2348" data-mp="wziok8ms0ix">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-2349" data-mp="3t10glk322s85">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2350" data-mp="alzkscifjc1c">
          <div className="flex items-center h-16" data-id="element-2351" data-mp="anarak39exw5">
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-2352" data-mp="5urf9rk80atg">LOGO</div>

            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-2353" data-mp="3eb2z1mzmt0rx">
              <Link data-phoebe-nav="/admin" to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2354" data-mp="3e8zxq6t13l7w">
                <Home size={18} data-id="element-2355" data-mp="1tkfeniqxozau" />
                <span className="hidden sm:inline" data-id="element-2356" data-mp="1tlpd491p89i0">Admin Hub</span>
              </Link>
              <Link data-phoebe-nav="/analytics" to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2357" data-mp="1fu27hilv9omk">
                <BarChart3 size={18} data-id="element-2358" data-mp="28ipvhly02s3t" />
                <span className="hidden sm:inline" data-id="element-2359" data-mp="2cj9runar1row">Analytics</span>
              </Link>
              <Link data-phoebe-nav="/feedback" to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2360" data-mp="1yjqyx1qiemx6">
                <FileText size={18} data-id="element-2361" data-mp="n4bytp307exu" />
                <span className="hidden sm:inline" data-id="element-2362" data-mp="n3ah51rhfu75">Reports</span>
              </Link>
              <Link data-phoebe-nav="/assessment" to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2363" data-mp="s59he2rxeazv">
                <UserCog size={18} data-id="element-2364" data-mp="yl1a2lwhdb8c" />
                <span className="hidden sm:inline" data-id="element-2365" data-mp="ym2xlpuonq6y">Users</span>
              </Link>
            </nav>

            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-2366" data-mp="tmzpfwjep3ur">
              <Link data-phoebe-nav="/alerts" to="/alerts" aria-label="Notifications" className="relative p-2 text-[#30718D] hover:text-[#265a70] transition-colors" data-id="element-2367" data-mp="1qbxp9t60jrys">
                <Bell size={20} data-id="element-2368" data-mp="1h9y54rzpc7rm" />
                {unreadCount > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-2369" data-mp="1cbd2klwz9c00"></span>}
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-2370" data-mp="5xnh0yvwkzti">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-2371" data-mp="wzrkhnbor1e">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-2372" data-mp="3rybe5pn2o2f3">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-2373" data-mp="5ja5v2ywql3v">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-2374" data-mp="9m6imd1rnmzw" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-2375" data-mp="jhgoxcz76gjh">
                    <div className="py-2" data-id="element-2376" data-mp="ofrtb86q3yub">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2377" data-mp="2l9cxqetzdcak">
                        <Users size={18} className="text-gray-600" data-id="element-2378" data-mp="2i78i2gvy5q8x" />
                        <span className="font-medium text-gray-900" data-id="element-2379" data-mp="2i9l3j40wyiqq">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2380" data-mp="2s7itbvyf8nlg">
                        <Home size={18} className="text-gray-600" data-id="element-2381" data-mp="316med5h5fd2l" />
                        <span className="font-medium text-gray-900" data-id="element-2382" data-mp="314t5djj2cgrf">
                          Learner Hub
                        </span>
                      </button>
                      <Link to="/admin" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2383" data-mp="3ave0gwzj335l">
                        <UserCog size={18} className="text-gray-600" data-id="element-2384" data-mp="3jxn9fif5eogv" />
                        <span className="font-medium text-gray-900" data-id="element-2385" data-mp="3fwbin9kjfx73">
                          Admin Hub
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-2386" data-mp="2clnzzdy80f4"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2387" data-mp="7cq8s1lw3hb5">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-2388" data-mp="3uhz3kwykf5r0">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-2389" data-mp="3auhwh3sztf3j"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-2390" data-mp="2wvrx1hipb3c0"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-2391" data-mp="31u3bk5jxy20k"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-2392" data-mp="3hc33loeek3uk">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="mx-auto max-w-[1440px] px-4 py-6 sm:px-6 sm:py-8 lg:px-8" data-id="element-2393" data-mp="33baa96zu7et1">
        <header data-phoebe-tour="alerts" className="mb-6" data-id="element-2394" data-mp="2ydquu3rekvzi">
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl" data-id="element-2395" data-mp="2tlz51vg2muaf">
            Notifications
          </h1>
          <p className="mt-1 text-sm text-gray-600 sm:text-base" data-id="element-2396" data-mp="2ome45fhccbv2">
            System notices, support replies, and account updates
          </p>
        </header>

        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4" data-id="element-2397" data-mp="2kj7qhmrwfiek">
          <div className="flex items-center gap-1" role="tablist" data-id="element-2398" data-mp="2fo264se5absl">
            {VIEWS.map(item => <button key={item.key} role="tab" aria-selected={view === item.key} onClick={() => setView(item.key)} className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${view === item.key ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-2399" data-mp="2aph6au2t1u4o">
                {item.label}
                {item.key === 'unread' && unreadCount > 0 && <span className="ml-1.5 text-xs" data-id="element-2400" data-mp="7gfek255chpn">({unreadCount})</span>}
              </button>)}
          </div>
          {unreadCount > 0 && <button onClick={markAllRead} className="flex items-center gap-1.5 self-start text-sm font-medium text-[#30718D] transition-colors hover:text-[#265a70] sm:self-auto" data-id="element-2401" data-mp="gehbo6lreq7n">
              <CheckCircle size={15} aria-hidden="true" data-id="element-2402" data-mp="3kt8rs1aftjl5" />
              Mark all as read
            </button>}
        </div>

        <div className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white" data-id="element-2403" data-mp="23pjb90ivgrmp">
          {visible.map(notification => <NotificationCard key={notification.id} notification={notification} onMarkRead={markRead} onArchive={archive} onRestore={restore} data-id="element-2404" data-mp="28nkqtbh3a8tf" />)}

          {visible.length === 0 && <p className="px-6 py-12 text-center text-sm text-gray-500" data-id="element-2405" data-mp="1kzl2sjb5wwij">
              {view === 'archived' ? 'Nothing archived yet.' : view === 'unread' ? 'You are all caught up.' : 'No notifications.'}
            </p>}
        </div>
      </main>
    </div>;
}